import type {UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {
  INSPECT_TABS,
  type InspectTab,
  type NavRoute,
  createDocumentNavRoute,
  createInspectIpfsNavRoute,
  createInspectNavRoute,
  type SiteSettingsTab,
} from '../routes'
import {
  activitySlugToFilter,
  extractViewTermFromUrl,
  parseCustomURL,
  routeToUrl,
  viewTermToRouteKey,
} from './entity-id-url'
import {appRouteOfId} from './navigation'
import {agentsRouteFromAbsoluteUrl} from './agents-routing'
import {unpackHmId} from './entity-id-url'

const EXPLORE_SORTS = ['relevance', 'recently_updated', 'newest', 'oldest', 'title'] as const
type ExploreSort = (typeof EXPLORE_SORTS)[number]

/**
 * Converts a directly parseable Hypermedia app URL into an application route.
 */
export function hypermediaUrlToRoute(url: string): NavRoute | null {
  if (/^(?:https?:\/\/[^/]+)?\/hm\/drafts\/?(?:[?#].*)?$/.test(url)) return {key: 'drafts'}
  // A link to the web agents UI (what "Copy trigger link" and friends produce) is an app route.
  const agentsRoute = agentsRouteFromAbsoluteUrl(url)
  if (agentsRoute) return agentsRoute
  const inspectIpfsMatch =
    // A raw ipfs:// URL opens the local IPFS inspector rather than bouncing out to a gateway.
    url.match(/^ipfs:\/\/([^?#]+)(?:[?#].*)?$/) ||
    url.match(/^hm:\/\/inspect\/ipfs\/([^?#]+)(?:[?#].*)?$/) ||
    url.match(/^((?:https?:\/\/[^/]+)?\/hm)\/inspect\/ipfs\/([^?#]+)(?:[?#].*)?$/) ||
    url.match(/^((?:https?:\/\/[^/]+)?)\/inspect\/ipfs\/([^?#]+)(?:[?#].*)?$/)
  if (inspectIpfsMatch) {
    const ipfsPath = inspectIpfsMatch.at(-1)
    return ipfsPath ? createInspectIpfsNavRoute(ipfsPath) : null
  }

  const {
    url: cleanUrl,
    isInspect,
    viewTerm,
    activityFilter,
    feedTab,
    commentId,
    accountUid,
    settingsTab,
    settingsDomain,
  } = extractViewTermFromUrl(url)
  const routeKey = viewTermToRouteKey(viewTerm)
  let id = unpackHmId(cleanUrl)
  if (!id) return null

  // On comment permalink URLs (/:comments/UID/TSID?v=CID), ?v is the comment
  // version, not the target document version — keep it off the document id.
  let commentVersion: string | null = null
  if (commentId && id.version) {
    commentVersion = id.version
    id = {...id, version: null, latest: true}
  }

  const query = parseCustomURL(cleanUrl)?.query
  if (routeKey === 'explore') {
    const sort =
      query?.sort && (EXPLORE_SORTS as readonly string[]).includes(query.sort) ? (query.sort as ExploreSort) : undefined
    return {
      key: 'explore',
      context: {type: 'site', id},
      q: query?.q || undefined,
      sort,
    }
  }
  const panelParam = query?.panel || null
  const effectivePanelParam =
    !panelParam && routeKey === 'activity' && activityFilter
      ? `activity/${activityFilter}`
      : !panelParam && routeKey === 'feed' && feedTab
        ? `feed/${feedTab}`
        : panelParam
  const inspectTabParam = query?.tab
  const inspectTab =
    isInspect && inspectTabParam && (INSPECT_TABS as readonly string[]).includes(inspectTabParam)
      ? (inspectTabParam as InspectTab)
      : null

  if (isInspect) {
    return createInspectNavRoute(id, routeKey, effectivePanelParam, commentId, accountUid, inspectTab)
  }

  if (routeKey || panelParam || commentId || accountUid) {
    const route = createDocumentNavRoute(
      id,
      routeKey,
      effectivePanelParam,
      commentId,
      accountUid,
      commentVersion,
      query?.reply,
    )
    if (route.key === 'comments' && query?.quote) {
      const range = query.quoteRange?.match(/^(\d+):(\d+)$/)
      const start = range ? Number(range[1]) : undefined
      const end = range ? Number(range[2]) : undefined
      return {
        ...route,
        targetBlockId: query.quote,
        blockId: query.quote,
        ...(start !== undefined &&
        end !== undefined &&
        Number.isSafeInteger(start) &&
        Number.isSafeInteger(end) &&
        end >= start
          ? {blockRange: {start, end}}
          : {}),
        isReplying: true,
        autoFocus: true,
      }
    }
    if (route.key === 'activity' && activityFilter) {
      return {
        ...route,
        filterEventType: activitySlugToFilter(activityFilter),
      }
    }
    if (route.key === 'site-settings' && settingsTab) {
      return {...route, tab: settingsTab as SiteSettingsTab, ...(settingsDomain ? {domain: settingsDomain} : {})}
    }
    return route
  }

  return appRouteOfId(id) || null
}

/**
 * Converts a parseable Hypermedia app URL into the browser-safe href for the
 * current web origin. Non-Hypermedia URLs are returned unchanged.
 */
export function hypermediaUrlToHref(
  url: string | null | undefined,
  options?: {
    hmUrlHref?: boolean
    originHomeId?: UnpackedHypermediaId
    origin?: string | null
  },
) {
  if (!url || options?.hmUrlHref) return url
  const route = hypermediaUrlToRoute(url)
  if (!route) return url
  return (
    routeToUrl(route, {
      hostname: options?.origin,
      originHomeId: options?.originHomeId,
    }) || url
  )
}
